import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Download, Copy, Check, ArrowLeft, QrCode, CreditCard, Share2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const SITE_URL = 'https://www.bicenter-consulting.com';

export const QRCodePage = () => {
  const { language, toggleLanguage } = useLanguage();
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [qrImageUrl, setQrImageUrl] = useState('');

  useEffect(() => {
    const url = `${API}/qr-code?url=${encodeURIComponent(SITE_URL)}&size=800`;
    fetch(url)
      .then(res => res.blob())
      .then(blob => {
        const objectUrl = URL.createObjectURL(blob);
        setQrImageUrl(objectUrl);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const handleDownload = async () => {
    try {
      const response = await fetch(`${API}/qr-code?url=${encodeURIComponent(SITE_URL)}&size=1200`);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'bicenter-consulting-qrcode.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(SITE_URL);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const texts = {
    fr: {
      title: 'QR Code Bicenter Consulting',
      subtitle: 'Scannez pour accéder directement à notre site',
      download: 'Télécharger le QR Code',
      downloadHD: 'Télécharger en haute qualité (PNG)',
      copyUrl: 'Copier le lien du site',
      copied: 'Lien copié !',
      back: 'Retour au site',
      usageTitle: 'Comment utiliser votre QR Code',
      usageCard: 'Carte de visite',
      usageCardDesc: 'Intégrez le QR Code sur vos cartes de visite pour un accès direct à votre site web professionnel.',
      usageSocial: 'Réseaux sociaux',
      usageSocialDesc: 'Partagez le QR Code sur LinkedIn, Instagram, Facebook pour rediriger vos contacts vers votre site.',
      usagePrint: 'Documents imprimés',
      usagePrintDesc: 'Ajoutez le QR Code à vos brochures, flyers et présentations commerciales.',
      tipTitle: 'Conseils d\'utilisation',
      tips: [
        'Taille minimale recommandée : 2 cm x 2 cm sur support imprimé',
        'Laissez un espace blanc autour du QR Code pour une meilleure lecture',
        'Testez le QR Code avec votre smartphone avant impression',
        'Le QR Code redirige vers : '
      ]
    },
    en: {
      title: 'Bicenter Consulting QR Code',
      subtitle: 'Scan to access our website directly',
      download: 'Download QR Code',
      downloadHD: 'Download in high quality (PNG)',
      copyUrl: 'Copy website link',
      copied: 'Link copied!',
      back: 'Back to website',
      usageTitle: 'How to use your QR Code',
      usageCard: 'Business Card',
      usageCardDesc: 'Add the QR Code to your business cards for direct access to your professional website.',
      usageSocial: 'Social Media',
      usageSocialDesc: 'Share the QR Code on LinkedIn, Instagram, Facebook to redirect contacts to your website.',
      usagePrint: 'Printed Materials',
      usagePrintDesc: 'Add the QR Code to your brochures, flyers and business presentations.',
      tipTitle: 'Usage Tips',
      tips: [
        'Minimum recommended size: 2 cm x 2 cm on printed materials',
        'Leave white space around the QR Code for better readability',
        'Test the QR Code with your smartphone before printing',
        'The QR Code redirects to: '
      ]
    }
  };

  const t = texts[language];

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8faf9] to-white" data-testid="qrcode-page">
      {/* Header */}
      <header className="bg-[#004d40] text-white py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a
            href="/"
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
            data-testid="qrcode-back-link"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{t.back}</span>
          </a>
          <div className="flex items-center gap-3">
            <span className="font-serif text-lg font-bold">
              BICENTER <span className="text-[#4caf50]">CONSULTING</span>
            </span>
            <button
              onClick={toggleLanguage}
              className="ml-4 px-3 py-1 rounded-full text-xs font-bold border border-white/30 hover:bg-white/10 transition-colors"
              data-testid="qrcode-lang-toggle"
            >
              {language === 'fr' ? 'EN' : 'FR'}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* Title */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-[#004d40]/5 text-[#004d40] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <QrCode className="w-4 h-4" />
            QR Code
          </div>
          <h1
            className="font-serif text-3xl sm:text-4xl font-bold text-[#004d40] mb-3"
            data-testid="qrcode-title"
          >
            {t.title}
          </h1>
          <p className="text-slate-600 text-base max-w-xl mx-auto">
            {t.subtitle}
          </p>
        </div>

        {/* QR Code Display */}
        <div className="flex flex-col items-center mb-12">
          <div
            className="bg-white rounded-2xl shadow-lg shadow-[#004d40]/5 border border-slate-100 p-8 mb-8"
            data-testid="qrcode-display"
          >
            {loading ? (
              <div className="w-[280px] h-[280px] flex items-center justify-center">
                <div className="spinner border-[#004d40] border-t-transparent" style={{ width: 40, height: 40, borderWidth: 4 }} />
              </div>
            ) : qrImageUrl ? (
              <img
                src={qrImageUrl}
                alt="Bicenter Consulting QR Code"
                className="w-[280px] h-[280px] object-contain"
                data-testid="qrcode-image"
              />
            ) : (
              <div className="w-[280px] h-[280px] flex items-center justify-center text-slate-400">
                <QrCode className="w-20 h-20" />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
            <button
              onClick={handleDownload}
              className="flex-1 flex items-center justify-center gap-2 bg-[#004d40] text-white px-6 py-3.5 rounded-full font-medium text-sm hover:bg-[#00695c] transition-colors"
              data-testid="qrcode-download-btn"
            >
              <Download className="w-5 h-5" />
              {t.download}
            </button>
            <button
              onClick={handleCopyUrl}
              className="flex-1 flex items-center justify-center gap-2 border-2 border-[#004d40] text-[#004d40] px-6 py-3.5 rounded-full font-medium text-sm hover:bg-[#004d40]/5 transition-colors"
              data-testid="qrcode-copy-btn"
            >
              {copied ? <Check className="w-5 h-5 text-[#4caf50]" /> : <Copy className="w-5 h-5" />}
              {copied ? t.copied : t.copyUrl}
            </button>
          </div>

          <p className="text-slate-400 text-xs mt-4">{t.downloadHD}</p>
        </div>

        {/* Usage Cards */}
        <div className="mb-12">
          <h2 className="font-serif text-2xl font-bold text-[#004d40] text-center mb-8">
            {t.usageTitle}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid="usage-card-business">
              <div className="w-12 h-12 rounded-xl bg-[#004d40]/5 flex items-center justify-center mb-4">
                <CreditCard className="w-6 h-6 text-[#004d40]" />
              </div>
              <h3 className="font-bold text-[#004d40] mb-2">{t.usageCard}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{t.usageCardDesc}</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid="usage-card-social">
              <div className="w-12 h-12 rounded-xl bg-[#4caf50]/10 flex items-center justify-center mb-4">
                <Share2 className="w-6 h-6 text-[#4caf50]" />
              </div>
              <h3 className="font-bold text-[#004d40] mb-2">{t.usageSocial}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{t.usageSocialDesc}</p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-shadow" data-testid="usage-card-print">
              <div className="w-12 h-12 rounded-xl bg-[#004d40]/5 flex items-center justify-center mb-4">
                <Download className="w-6 h-6 text-[#004d40]" />
              </div>
              <h3 className="font-bold text-[#4caf50] mb-2">{t.usagePrint}</h3>
              <p className="text-slate-600 text-sm leading-relaxed">{t.usagePrintDesc}</p>
            </div>
          </div>
        </div>

        {/* Tips */}
        <div className="bg-[#004d40]/[0.03] rounded-2xl p-8 border border-[#004d40]/10">
          <h3 className="font-bold text-[#004d40] mb-4">{t.tipTitle}</h3>
          <ul className="space-y-3">
            {t.tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                <div className="w-5 h-5 rounded-full bg-[#4caf50] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check className="w-3 h-3 text-white" />
                </div>
                <span>
                  {tip}
                  {i === t.tips.length - 1 && (
                    <span className="font-medium text-[#004d40]">{SITE_URL}</span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-slate-100 py-6 text-center">
        <p className="text-slate-400 text-sm">
          &copy; {new Date().getFullYear()} Bicenter Consulting. {language === 'fr' ? 'Tous droits réservés.' : 'All rights reserved.'}
        </p>
      </footer>
    </div>
  );
};
